import { combineReducers } from "redux";
import { createSelector } from "reselect";
import * as R from "ramda";

import { absolutePageToRelative, pdfToCssUnits } from "~/utils";

import {
  selectPagesCount,
  selectWidth,
  selectHeight,
  selectPrintablePages
} from "~/store/ducks/domain/config";

import { selectIsGreaterThanMedium } from "~/store/ducks/application/browser";
import { selectIsSidebarRightVisible } from "~/store/ducks/domain/ui/sidebarRight";
import { selectIsSidebarLeftVisible } from "~/store/ducks/domain/ui/sidebarLeft";
import { selectShowPrintButton } from "~/store/ducks/domain/ui/menu";

import {
  selectAbsolutePage,
  selectLeftSidebarIsOpen,
  selectRightSidebarIsOpen
} from "~/store/ducks/user/uistate";

import { selectDisplayedPages } from "~/store/selectors/page";

import application from "./application";
import domain from "./domain";
import user from "./user";

const reducer = combineReducers({
  application,
  domain,
  user
});

export const selectRelativePage = createSelector(selectAbsolutePage, page =>
  absolutePageToRelative(page)
);

export const selectIsFirstPage = createSelector(selectAbsolutePage, page =>
  R.equals(page, 1)
);

export const selectIsLastPage = createSelector(
  selectAbsolutePage,
  selectPagesCount,
  (page, pagesCount) => R.equals(page, pagesCount)
);

// FIXME: move to selectors/page and reimplement via index
export const selectIsNotFirstPage = R.complement(selectIsFirstPage);
export const selectIsNotLastPage = R.complement(selectIsLastPage);

// export const selectIsNeitherFirstNorLastPage = R.both(
//   selectIsNotFirstPage,
//   selectIsNotLastPage
// );

export const selectTotalPageWidth = createSelector(selectWidth, pdfToCssUnits);

export const selectTotalPageHeight = createSelector(
  selectHeight,
  pdfToCssUnits
);

export const selectPadSidebarLeft = createSelector(
  selectIsGreaterThanMedium,
  selectIsSidebarLeftVisible,
  selectLeftSidebarIsOpen,
  (isGreaterThanMedium, isVisible, isOpen) => {
    return isGreaterThanMedium && isVisible && isOpen;
  }
);

export const selectPadSidebarRight = createSelector(
  selectIsGreaterThanMedium,
  selectIsSidebarRightVisible,
  selectRightSidebarIsOpen,
  (isGreaterThanMedium, isVisible, isOpen) => {
    return isGreaterThanMedium && isVisible && isOpen;
  }
);

export const selectIsAbleToPrint = createSelector(
  selectDisplayedPages,
  selectPrintablePages,
  selectShowPrintButton,
  (activePages, printablePages, showPrintButton) => {
    const currentPages = R.sort(R.subtract, activePages);
    const canPrintSinglePages = !R.isEmpty(
      R.intersection(printablePages, currentPages)
    );
    const canPrintAllPages = R.isEmpty(printablePages);

    return showPrintButton && (canPrintSinglePages || canPrintAllPages);
  }
);

export default reducer;
